import React from "react";
import { Award, CircleDollarSign, Star, Swords, TriangleAlert } from "lucide-react";
import { Delta, InfoPopover } from "@/components/vb";
import type { Big5Category } from "@/types/vb";
import { BIG5_LABELS, type CategoryCoverage } from "./big5Coverage";
import { METRIC_INFO } from "../metricCopy";

const ICONS: Record<Big5Category, React.ReactNode> = {
  cost: <CircleDollarSign aria-hidden="true" />,
  problems: <TriangleAlert aria-hidden="true" />,
  head_to_head: <Swords aria-hidden="true" />,
  reviews: <Star aria-hidden="true" />,
  best_of: <Award aria-hidden="true" />,
};

const TONE_CLASS: Record<CategoryCoverage["tone"], string> = {
  full: "vb-b5--full",
  partial: "vb-b5--partial",
  none: "vb-b5--none",
};

/** Format a 0–1 fraction as a whole-percent string ("64%"). */
function pct(v: number | null): string {
  return v == null ? "-" : `${Math.round(v * 100)}%`;
}

type Props = {
  coverage: CategoryCoverage[];
  /** Visibility / SoV for the latest run (0–1), derived from the run result. */
  visibility: number | null;
  sov: number | null;
  /** Run-over-run visibility change (from the buyer's cross-run metrics). */
  visibilityDelta: number | null;
  questionCount: number;
  /** The question set changed since the run, so coverage undercounts - nudge. */
  questionsChanged?: boolean;
};

/** Big-5 coverage tiles + headline mini-stats (proto .big5 + .mini-stats).
 *  Coverage = focus-surfaced-per-category from the latest DONE run; visibility /
 *  SoV are derived from that same run. No invented metrics. */
export function Big5CoverageCard({
  coverage,
  visibility,
  sov,
  visibilityDelta,
  questionCount,
  questionsChanged,
}: Props) {
  // Header hint summarises the tiles below: the number of distinct questions the
  // focus company surfaced in, across all Big-5 topics (the sum of the tile
  // numerators).
  const surfacedTotal = coverage.reduce((n, c) => n + c.surfaced, 0);

  return (
    <div className="vb-dcard">
      <div className="vb-dcard__hd">
        <span className="vb-dcard__lbl">Big 5 coverage</span>
        <InfoPopover label="About Big 5 coverage">{METRIC_INFO.big5}</InfoPopover>
        <span className="vb-dcard__hint">surfaced in {surfacedTotal} of your questions</span>
      </div>

      <div className="vb-big5">
        {coverage.map((c) => (
          <div className={`vb-b5 ${TONE_CLASS[c.tone]}`} key={c.category}>
            <div className="vb-b5__hd">
              {ICONS[c.category]}
              {BIG5_LABELS[c.category]}
            </div>
            <div className="vb-b5__ct tnum">
              <span className="vb-b5__dot" />
              {c.surfaced}/{c.total}
            </div>
          </div>
        ))}
      </div>

      {questionsChanged && (
        <div className="vb-cov-note" role="status">
          Questions changed since the last run. Re-run the analysis to refresh coverage.
        </div>
      )}

      <div className="vb-mini-stats">
        <div className="vb-ms">
          <span className="vb-ms__k">
            Visibility
            <InfoPopover label="About visibility">{METRIC_INFO.visibility}</InfoPopover>
          </span>
          <span className="vb-ms__v">
            <span className="tnum">{pct(visibility)}</span>
            <Delta value={visibilityDelta == null ? null : Math.round(visibilityDelta * 100)} />
          </span>
        </div>
        <div className="vb-ms">
          <span className="vb-ms__k">
            Share of voice
            <InfoPopover label="About share of voice">{METRIC_INFO.sov}</InfoPopover>
          </span>
          <span className="vb-ms__v">
            <span className="tnum">{pct(sov)}</span>
            <small>of companies mentioned</small>
          </span>
        </div>
        <div className="vb-ms">
          <span className="vb-ms__k">Questions</span>
          <span className="vb-ms__v">
            <span className="tnum">{questionCount}</span>
            <small>across the Big 5</small>
          </span>
        </div>
      </div>
    </div>
  );
}

export default Big5CoverageCard;
